import { Drawer, Form, Select, DatePicker, InputNumber, Button, Space, Row, Col, Divider, Typography } from 'antd';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;
const { Text } = Typography;

export interface UserFilterValues {
  search?: string;
  phone?: string;
  role?: string;
  status?: string;
  isDriverVerified?: boolean;
  minTrips?: number;
  maxTrips?: number;
  minRating?: number;
  maxRating?: number;
  registeredFrom?: string;
  registeredTo?: string;
  hasVehicle?: boolean;
  blockedOnly?: boolean;
  hasCompletedTrips?: boolean;
}

interface UserFiltersProps {
  open: boolean;
  onClose: () => void;
  onApply: (values: UserFilterValues) => void;
  onReset: () => void;
  filterType: 'all' | 'passengers' | 'drivers';
  initialValues?: UserFilterValues;
}

export const UserFilters = ({
  open,
  onClose,
  onApply,
  onReset,
  filterType,
  initialValues,
}: UserFiltersProps) => {
  const [form] = Form.useForm();

  const handleApply = () => {
    const values = form.getFieldsValue();
    const dateRange = values.dateRange;

    const filters: UserFilterValues = {
      ...values,
      registeredFrom: dateRange?.[0]?.format('YYYY-MM-DD'),
      registeredTo: dateRange?.[1]?.format('YYYY-MM-DD'),
    };
    delete (filters as any).dateRange;

    // Remove empty values
    Object.keys(filters).forEach((key) => {
      if (filters[key as keyof UserFilterValues] === undefined ||
          filters[key as keyof UserFilterValues] === null ||
          filters[key as keyof UserFilterValues] === '') {
        delete filters[key as keyof UserFilterValues];
      }
    });

    onApply(filters);
    onClose();
  };

  const handleReset = () => {
    form.resetFields();
    onReset();
    onClose();
  };

  return (
    <Drawer
      title={
        <Space>
          <FilterOutlined />
          <span>Kengaytirilgan filtr</span>
        </Space>
      }
      placement="right"
      width={420}
      onClose={onClose}
      open={open}
    >
      {/* Action buttons below header */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <Button icon={<ClearOutlined />} onClick={handleReset} style={{ flex: 1 }}>
          Tozalash
        </Button>
        <Button type="primary" onClick={handleApply} style={{ flex: 1 }}>
          Qo'llash
        </Button>
      </div>

      <Divider style={{ marginTop: 0 }} />

      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
      >
        {/* Verification Status - for 'all' and 'drivers' */}
        {(filterType === 'all' || filterType === 'drivers') && (
          <>
            <Text strong style={{ display: 'block', marginBottom: 16 }}>Tasdiqlash holati</Text>
            <Form.Item label="Haydovchi tasdiqlangan" name="isDriverVerified">
              <Select
                allowClear
                placeholder="Barchasi"
                options={[
                  { value: true, label: 'Tasdiqlangan' },
                  { value: false, label: 'Tasdiqlanmagan' },
                ]}
              />
            </Form.Item>
            <Divider />
          </>
        )}

        {/* Trip Statistics */}
        <Text strong style={{ display: 'block', marginBottom: 16 }}>Sayohat statistikasi</Text>

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>Sayohatlar soni</div>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item name="minTrips" noStyle>
                <InputNumber
                  placeholder="Minimal"
                  min={0}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maxTrips" noStyle>
                <InputNumber
                  placeholder="Maksimal"
                  min={0}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Form.Item label="Sayohat qilganmi" name="hasCompletedTrips">
          <Select
            allowClear
            placeholder="Barchasi"
            options={[
              { value: true, label: 'Ha, sayohat qilgan' },
              { value: false, label: "Hali sayohat qilmagan" },
            ]}
          />
        </Form.Item>

        {/* Rating range - for 'all' and 'drivers' */}
        {(filterType === 'all' || filterType === 'drivers') && (
          <>
            <Divider />
            <Text strong style={{ display: 'block', marginBottom: 16 }}>Reyting</Text>
            <div style={{ marginBottom: 16 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="minRating" noStyle>
                    <InputNumber
                      placeholder="Minimal (1.0)"
                      min={1}
                      max={5}
                      step={0.1}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="maxRating" noStyle>
                    <InputNumber
                      placeholder="Maksimal (5.0)"
                      min={1}
                      max={5}
                      step={0.1}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </>
        )}

        <Divider />

        {/* Registration date range */}
        <Text strong style={{ display: 'block', marginBottom: 16 }}>Ro'yxatdan o'tgan sana</Text>
        <Form.Item name="dateRange">
          <RangePicker
            style={{ width: '100%' }}
            placeholder={['Boshlanish', 'Tugash']}
            format="YYYY-MM-DD"
          />
        </Form.Item>

        {/* Blocked filter */}
        <Form.Item label="Bloklangan foydalanuvchilar" name="blockedOnly">
          <Select
            allowClear
            placeholder="Barchasi"
            options={[
              { value: true, label: 'Faqat bloklangan' },
              { value: false, label: 'Faqat bloklanmagan' },
            ]}
          />
        </Form.Item>

        {/* Has vehicle - for drivers only */}
        {filterType === 'drivers' && (
          <>
            <Divider />
            <Text strong style={{ display: 'block', marginBottom: 16 }}>Avtomobil</Text>
            <Form.Item label="Avtomobil holati" name="hasVehicle">
              <Select
                allowClear
                placeholder="Barchasi"
                options={[
                  { value: true, label: "Avtomobili ro'yxatdan o'tgan" },
                  { value: false, label: "Avtomobili yo'q" },
                ]}
              />
            </Form.Item>
          </>
        )}
      </Form>
    </Drawer>
  );
};
